const SELECTORS = {
  container: "[data-carousel-container]",
  nav: "[data-carousel-nav]",
  prevButton: "[data-carousel-prev]",
  nextButton: "[data-carousel-next]",
  cards: "[data-carousel-card]",
};

// Sub-Pixel-Toleranz: calc()-Card-Breiten füllen den Track exakt aus, wodurch
// getBoundingClientRect()-Floats durch Rundung minimal über die Container-Kante
// ragen. Ohne diese Toleranz bliebe der Next-Button im "passt exakt"-Fall aktiv.
const OVERFLOW_EPSILON = 1;

class TestimonialsCarousel extends HTMLElement {
  constructor() {
    super();
    this.container = this.querySelector(SELECTORS.container);
    this.nav = this.querySelector(SELECTORS.nav);
    this.prevButton = this.querySelector(SELECTORS.prevButton);
    this.nextButton = this.querySelector(SELECTORS.nextButton);
    this.cards = Array.from(this.querySelectorAll(SELECTORS.cards));
    this.currentIndex = 0;
  }

  connectedCallback() {
    if (!this.container || this.cards.length === 0) return;

    this.setupNavigation();
    this.updateButtonStates();

    this.boundOnChange = this.onChange.bind(this);
    this.debouncedScrollHandler = this.createDebouncedScrollHandler();

    if ("onscrollend" in this.container) {
      this.container.addEventListener("scrollend", () => {
        this.boundOnChange();
      });
    } else {
      this.container.addEventListener("scroll", () => {
        this.debouncedScrollHandler();
      });
    }

    window.addEventListener("resize", () => {
      this.boundOnChange();
    });
  }

  disconnectedCallback() {
    if ("onscrollend" in this.container) {
      this.container.removeEventListener("scrollend", this.boundOnChange);
    } else {
      this.container.removeEventListener("scroll", this.debouncedScrollHandler);
    }

    window.removeEventListener("resize", this.boundOnChange);
  }

  createDebouncedScrollHandler() {
    let timeout;

    return () => {
      clearTimeout(timeout);
      timeout = setTimeout(() => {
        this.onChange();
      }, 100);
    };
  }

  onChange() {
    this.updateIndex();
    this.updateButtonStates();
  }

  setupNavigation() {
    this.prevButton?.addEventListener("click", () => this.navigate(-1));
    this.nextButton?.addEventListener("click", () => this.navigate(1));
  }

  navigate(direction) {
    let newIndex = this.currentIndex + direction;

    if (newIndex < 0) {
      newIndex = 0;
    } else if (newIndex >= this.cards.length) {
      newIndex = this.cards.length - 1;
    }

    this.currentIndex = newIndex;
    this.scrollToCard(this.currentIndex);
    this.updateButtonStates();
  }

  updateIndex() {
    const containerRect = this.container.getBoundingClientRect();
    let closestCard = null;
    let smallestDistance = Infinity;

    this.cards.forEach((card) => {
      const cardRect = card.getBoundingClientRect();

      const distance = Math.abs(cardRect.left - containerRect.left);

      if (distance < smallestDistance) {
        smallestDistance = distance;
        closestCard = card;
      }
    });

    if (!closestCard) return;

    this.currentIndex = this.cards.indexOf(closestCard);
  }

  scrollToCard(index) {
    const card = this.cards[index];
    if (!card) return;
    const containerRect = this.container.getBoundingClientRect();
    const cardRect = card.getBoundingClientRect();

    this.container.scrollTo({
      left: this.container.scrollLeft + cardRect.left - containerRect.left,
      behavior: "smooth",
    });
  }

  updateButtonStates() {
    if (!this.prevButton || !this.nextButton || !this.cards.length) return;

    const firstCard = this.cards[0];
    const lastCard = this.cards[this.cards.length - 1];

    if (!firstCard || !lastCard) return;

    const firstCardRect = firstCard.getBoundingClientRect();
    const lastCardRect = lastCard.getBoundingClientRect();
    const containerRect = this.container.getBoundingClientRect();

    const scrollToLeftButtonIsVisible =
      firstCardRect.left < containerRect.left - OVERFLOW_EPSILON;
    const scrollToRightButtonIsVisible =
      lastCardRect.right > containerRect.right + OVERFLOW_EPSILON;

    this.prevButton.disabled = !scrollToLeftButtonIsVisible;
    this.nextButton.disabled = !scrollToRightButtonIsVisible;

    // Navigation komplett ausblenden, wenn in keine Richtung gescrollt werden
    // kann (beide Buttons wären disabled). Bewusst an die Card-Wrapper-Rects
    // gekoppelt statt an container.scrollWidth: Die gedrehten inneren Cards
    // blähen scrollWidth künstlich auf und würden sonst dauerhaft "scrollbar"
    // signalisieren.
    if (this.nav) {
      this.nav.hidden =
        !scrollToLeftButtonIsVisible && !scrollToRightButtonIsVisible;
    }
  }
}

customElements.define("testimonials-carousel", TestimonialsCarousel);